import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { sanitiseString } from '@quickstock/shared';

import { mutate } from '../lib/mutate';
import { Field, inputClass, Modal } from './Modal';
import { StoredNameHint } from './StoredNameHint';
import { formatCurrency } from '../lib/format';

export type AccountSide = 'customers' | 'suppliers';

export interface EditableAccount {
  id: number;
  name: string;
  contact: string | null;
  creditPeriodDays: number;
  creditLimit: string;
  currentCredit: string;
}

/**
 * Creates or edits a credit customer or supplier.
 *
 * The two are almost the same form and deliberately not the same rules. The
 * database holds customers to `creditlimit > 0` and suppliers to `>= 0`, and
 * requires a contact number for a customer but not for a supplier — a supplier
 * who wants paying up front, with no account, is an ordinary arrangement. The
 * form reflects that rather than imposing the stricter rule on both, which
 * would reject valid suppliers.
 *
 * Two fields are held rather than left uncontrolled, and both for the same
 * reason — the till stores a particular form and the cloud must not store a
 * different one. The name is normalised as it is typed, with the stored form
 * shown underneath; the contact number takes digits only, which is what the
 * till's own form allows through its keydown and paste handlers.
 */
export function CreditAccountDialog({
  storeId,
  side,
  account,
  onClose,
}: {
  storeId: string;
  side: AccountSide;
  /** Null when adding. */
  account: EditableAccount | null;
  onClose: () => void;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState(account?.name ?? '');
  const [contact, setContact] = useState(account?.contact ?? '');

  const isCustomer = side === 'customers';
  const isEditing = account !== null;
  const noun = isCustomer ? 'credit customer' : 'supplier';

  const save = useMutation({
    mutationFn: (body: {
      name: string;
      contact: string;
      creditPeriodDays: number;
      creditLimit: number;
    }) =>
      isEditing
        ? mutate.patch(`/stores/${storeId}/credit/${side}/${account.id}`, body)
        : mutate.post(`/stores/${storeId}/credit/${side}`, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['credit', storeId] });
      void queryClient.invalidateQueries({ queryKey: ['credit-summary', storeId] });
      setError(null);
      onClose();
    },
    onError: (caught: unknown) =>
      setError(caught instanceof Error ? caught.message : 'Could not save.'),
  });

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setError(null);

    const form = new FormData(event.currentTarget);
    save.mutate({
      name: sanitiseString(name),
      contact,
      creditPeriodDays: Number(form.get('creditPeriodDays')),
      creditLimit: Number(form.get('creditLimit')),
    });
  }

  return (
    <Modal open title={isEditing ? `Edit ${noun}` : `Add a ${noun}`} onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <Field label="Name" htmlFor="name">
          <input
            id="name"
            name="name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            className={inputClass}
            maxLength={50}
            required
            autoFocus
          />
        </Field>

        <StoredNameHint name={name} />

        <Field
          label="Contact"
          htmlFor="contact"
          hint={
            isCustomer ? 'Digits only.' : 'Digits only, and optional for a supplier.'
          }
        >
          <input
            id="contact"
            name="contact"
            value={contact}
            /* Stripped rather than blocked at the keystroke. The till filters
               keydown and paste, which is enough for a keyboard at a counter but
               misses what a phone keyboard, an autofill or a drag-and-drop can
               put in a field — and any of those would store a number the till
               would never have accepted for the same person. */
            onChange={(event) => setContact(event.target.value.replace(/[^0-9]/g, ''))}
            className={`${inputClass} qs-figure`}
            maxLength={12}
            inputMode="numeric"
            required={isCustomer}
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Credit period" htmlFor="creditPeriodDays" hint="Days until due.">
            <input
              id="creditPeriodDays"
              name="creditPeriodDays"
              type="number"
              min="0"
              max="365"
              defaultValue={account?.creditPeriodDays ?? 30}
              className={`${inputClass} qs-figure`}
              required
            />
          </Field>

          <Field
            label="Credit limit"
            htmlFor="creditLimit"
            hint={
              isCustomer
                ? 'Must be above zero.'
                : 'Zero means payment up front.'
            }
          >
            <input
              id="creditLimit"
              name="creditLimit"
              type="number"
              step="0.01"
              min={isCustomer ? '0.01' : '0'}
              defaultValue={account?.creditLimit ?? (isCustomer ? '100.00' : '0.00')}
              className={`${inputClass} qs-figure`}
              required
            />
          </Field>
        </div>

        {isEditing && Number(account.currentCredit) > 0 && (
          // The server refuses a limit below what is already owed; saying so up
          // front is better than letting them find out by being rejected.
          <p className="mb-3 rounded border border-rule bg-sunken px-2.5 py-2 text-2xs text-ink-muted">
            {formatCurrency(account.currentCredit)} is currently outstanding on this account. The
            limit cannot be set below that.
          </p>
        )}

        {error && (
          <p role="alert" className="mb-3 text-xs text-down">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-rule px-3 py-1.5 text-xs text-ink-muted hover:text-ink"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={save.isPending}
            className="rounded bg-accent px-3 py-1.5 text-xs font-medium text-ink-inverse hover:bg-accent-hover disabled:opacity-60"
          >
            {save.isPending ? 'Saving…' : isEditing ? 'Save' : 'Add'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
